/**
 * API client for team management endpoints.
 * Routes: /api/mgmt/teams
 */

import { apiFetch } from './client';

export interface Team {
  teamId: string;
  name: string;
  description: string;
  maxBudget?: number;
  members: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateTeamData {
  name: string;
  description: string;
  maxBudget?: number;
  members?: string[];
}

export interface UpdateTeamData {
  name?: string;
  description?: string;
  maxBudget?: number;
  members?: string[];
}

export async function listTeams(token: string): Promise<Team[]> {
  const data = await apiFetch<{ teams: Team[] }>('/api/mgmt/teams', token);
  return Array.isArray(data) ? data : (data.teams ?? []);
}

export async function getTeam(teamId: string, token: string): Promise<Team> {
  const data = await apiFetch<{ team: Team }>(`/api/mgmt/teams/${encodeURIComponent(teamId)}`, token);
  return data.team ?? data as unknown as Team;
}

export async function createTeam(data: CreateTeamData, token: string): Promise<Team> {
  const resp = await apiFetch<{ team: Team }>('/api/mgmt/teams', token, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return resp.team ?? resp as unknown as Team;
}

export async function updateTeam(
  teamId: string,
  data: UpdateTeamData,
  token: string,
): Promise<Team> {
  const resp = await apiFetch<{ team: Team }>(`/api/mgmt/teams/${encodeURIComponent(teamId)}`, token, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return resp.team ?? resp as unknown as Team;
}

export async function addMember(
  teamId: string,
  username: string,
  token: string,
): Promise<Team> {
  const resp = await apiFetch<{ team: Team }>(`/api/mgmt/teams/${encodeURIComponent(teamId)}/members`, token, {
    method: 'POST',
    body: JSON.stringify({ username }),
  });
  return resp.team ?? resp as unknown as Team;
}

export async function removeMember(
  teamId: string,
  username: string,
  token: string,
): Promise<Team> {
  const resp = await apiFetch<{ team: Team }>(
    `/api/mgmt/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(username)}`,
    token,
    { method: 'DELETE' },
  );
  return resp.team ?? resp as unknown as Team;
}
